import React, { useRef, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Dimensions,
  Platform,
  LayoutChangeEvent,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { IBulletinTableRow } from '@/types/admin/bulletins';
import {
  formatBulletinDate,
  getRecipientTypeLabel,
} from '@/utils/admin/bulletinUtils';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { SVG_COMPONENTS } from '@/assets/admin';
import BulletinImagesCarousel from '@/components/common/bulletins/BulletinImagesCarousel';
import BulletinDescription from '@/components/common/bulletins/BulletinDescription';
import { useRemoteImageAspect } from '@/hooks/common/useRemoteImageAspect';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { GlassCard } from '@/components/shared/redesign';
import { GlassStatusBadge } from '@/components/admin/redesign';

const { width: screenWidth } = Dimensions.get('window');

const GRADIENT_COLORS = [
  PRIMITIVE_COLORS.resioBlue,
  PRIMITIVE_COLORS.resioPurple,
  PRIMITIVE_COLORS.resioPink,
] as const;
const GRADIENT_LOCATIONS = [0.1138, 0.6385, 0.9773] as const;
const SNAP_GAP = 0;

interface BulletinCardProps {
  bulletin: IBulletinTableRow;
  onEdit?: (bulletin: IBulletinTableRow) => void;
  isExpanded?: boolean;
  onToggleExpanded?: (bulletinId: number) => void;
  feedFilter?: string;
  variant?: 'admin' | 'resident';
}

const BulletinCardBase: React.FC<BulletinCardProps> = ({
  bulletin,
  onEdit,
  isExpanded = false,
  onToggleExpanded,
  feedFilter,
  variant = 'admin',
}) => {
  const { redesign } = useAdminColors();
  const scrollViewRef = useRef<ScrollView>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageContainerWidth, setImageContainerWidth] = useState<number>(0);
  const [imageAspect, setImageAspect] = useState<number | null>(null);

  const description = bulletin.description || '';
  const shouldTruncate = description.length > 150;
  const displayDescription = shouldTruncate && !isExpanded
    ? `${description.slice(0, 150)}...`
    : description;

  const author = bulletin.author;
  const authorName = author?.firstName
    ? `${author.firstName} ${author.lastName || ''}`.trim()
    : author?.email;

  const files = bulletin.files || [];
  const hasImages = files && files.length > 0;
  const imageCount = files.length;

  const isDraft = feedFilter === 'Draft';
  const isScheduled = feedFilter === 'Scheduled';
  const isEditable = isDraft || isScheduled;
  const isResidentVariant = variant === 'resident';

  const measuredAspect = useRemoteImageAspect(files?.[currentImageIndex]?.fileUrl || null);
  React.useEffect(() => {
    setImageAspect(measuredAspect);
  }, [measuredAspect]);

  const onImageContainerLayout = (e: LayoutChangeEvent) => {
    setImageContainerWidth(e.nativeEvent.layout.width);
  };

  const minImageHeight = 200;
  const maxImageHeight = Platform.OS === 'web' ? 360 : 300;
  const computeHeight = (effectiveWidth: number) => {
    return imageAspect && effectiveWidth
      ? Math.min(maxImageHeight, Math.max(minImageHeight, Math.round(effectiveWidth * imageAspect)))
      : minImageHeight;
  };

  const scrollToImage = (index: number) => {
    if (scrollViewRef.current) {
      const containerW = imageContainerWidth || (screenWidth - 64);
      const frameW = containerW;
      const scrollToX = index * (frameW + SNAP_GAP);
      scrollViewRef.current.scrollTo({ x: scrollToX, animated: true });
      setCurrentImageIndex(index);
    }
  };

  const sanitizeChildren = (children: React.ReactNode[]) => {
    return React.Children.toArray(children).filter((child) => {
      if (typeof child === 'string') {
        return child.trim() !== '';
      }
      return child !== null && child !== undefined;
    });
  };

  const renderHeaderDateText = () => {
    if (isResidentVariant) {
      const baseDate = bulletin.publishDate ? new Date(bulletin.publishDate) : bulletin.date;
      return baseDate ? formatBulletinDate(baseDate) : '';
    }
    if (isDraft) return '';
    if (isScheduled) {
      const iso = bulletin.publishDate || undefined;
      if (iso) {
        const d = new Date(iso);
        const datePart = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        const timePart = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
        return `Scheduled at ${datePart} ${timePart}`;
      }
      return 'Scheduled';
    }
    return formatBulletinDate(bulletin.date);
  };

  const headerElement = (
    <BulletinHeader
      variant={variant}
      isDraft={isDraft}
      isScheduled={isScheduled}
      renderHeaderDateText={renderHeaderDateText}
      bulletin={bulletin}
      isEditable={isEditable}
      onEdit={onEdit}
    />
  );

  const titleElement = (
    <Text style={[styles.title, { color: redesign.text.primary }]}>
      {bulletin.title || bulletin.subject || 'Untitled'}
    </Text>
  );

  const imagesElement = hasImages ? (
    <BulletinImagesCarousel
      files={files}
      computeHeight={computeHeight}
      maxImageHeight={maxImageHeight}
    />
  ) : null;

  const descriptionElement = description ? (
    <BulletinDescription
      text={description}
      expanded={isExpanded}
      onToggle={() => onToggleExpanded && onToggleExpanded(bulletin.id)}
    />
  ) : null;

  const authorElement = authorName && !isResidentVariant ? (
    <Text style={[styles.authorText, { color: redesign.text.muted }]}>
      {`Author - ${authorName}`}
    </Text>
  ) : null;

  const contentChildren = sanitizeChildren([
    headerElement,
    titleElement,
    imagesElement,
    descriptionElement,
    authorElement,
  ]);

  return (
    <TouchableOpacity style={styles.touchableContainer} activeOpacity={0.7}>
      <View style={styles.cardContainer}>
        {bulletin.highlight && (
          <LinearGradient
            colors={[GRADIENT_COLORS[0], GRADIENT_COLORS[1], GRADIENT_COLORS[2]]}
            locations={[GRADIENT_LOCATIONS[0], GRADIENT_LOCATIONS[1], GRADIENT_LOCATIONS[2]]}
            start={{ x: 0, y: 0 }}
            end={{ x: 0, y: 1 }}
            style={styles.gradientBorder}
          />
        )}

        <GlassCard
          borderRadius={bulletin.highlight ? 6 : 8}
          padding={16}
          style={[
            styles.contentContainer,
            bulletin.highlight && styles.contentContainerWithBorder,
          ]}
        >
          {contentChildren.map((child, index) => {
            if (React.isValidElement(child)) {
              const existingKey = (child as { key?: string }).key;
              return React.cloneElement(child as React.ReactElement, {
                key: existingKey ?? `content-${index}`,
              } as { key: string });
            }
            return (
              <React.Fragment key={`content-${index}`}>{child}</React.Fragment>
            );
          })}
        </GlassCard>
      </View>
    </TouchableOpacity>
  );
};

const areEqual = (prev: BulletinCardProps, next: BulletinCardProps) => {
  const a = prev.bulletin;
  const b = next.bulletin;
  if (a.id !== b.id) return false;
  if (prev.isExpanded !== next.isExpanded) return false;
  if (prev.feedFilter !== next.feedFilter) return false;
  if (prev.variant !== next.variant) return false;
  if (a.title !== b.title) return false;
  if (a.subject !== b.subject) return false;
  if ((a.description || '') !== (b.description || '')) return false;
  if (a.highlight !== b.highlight) return false;
  if (a.pinHomepage !== b.pinHomepage) return false;
  if ((a.publishDate || null) !== (b.publishDate || null)) return false;
  if (a.messageReadCount !== b.messageReadCount) return false;
  if ((a.recipientType || '') !== (b.recipientType || '')) return false;
  const aFilesLen = (a.files && a.files.length) || 0;
  const bFilesLen = (b.files && b.files.length) || 0;
  if (aFilesLen !== bFilesLen) return false;
  return true;
};

export const BulletinCard = React.memo(BulletinCardBase, areEqual);

type BulletinHeaderProps = {
  variant: 'admin' | 'resident';
  isDraft: boolean;
  isScheduled: boolean;
  renderHeaderDateText: () => string;
  bulletin: IBulletinTableRow;
  isEditable: boolean;
  onEdit?: (b: IBulletinTableRow) => void;
};

const BulletinHeader: React.FC<BulletinHeaderProps> = ({
  variant,
  isDraft,
  isScheduled,
  renderHeaderDateText,
  bulletin,
  isEditable,
  onEdit,
}) => {
  const { redesign } = useAdminColors();
  const isResidentVariant = variant === 'resident';
  const badgePillBg = redesign.glass.buttonBg;

  return (
    <View style={styles.header}>
      <View style={styles.headerTop}>
        <View style={styles.leftSection}>
          {isResidentVariant ? (
            renderHeaderDateText() ? (
              <Text style={[styles.dateText, { color: redesign.text.muted }]}>
                {renderHeaderDateText()}
              </Text>
            ) : null
          ) : (
            <>
              {!isDraft && (
                <Text
                  style={[styles.dateText, { color: redesign.text.muted }]}
                  numberOfLines={1}
                >
                  {renderHeaderDateText()}
                </Text>
              )}
              {isDraft && <GlassStatusBadge label="DRAFT" variant="warning" />}
              {isScheduled && <GlassStatusBadge label="SCHEDULED" variant="info" />}
            </>
          )}
        </View>
        <View style={styles.rightSection}>
          {isResidentVariant ? (
            <View style={styles.badgesContainer}>
              {bulletin.pinHomepage && (
                <View style={[styles.pinBadge, { backgroundColor: badgePillBg }]}>
                  <SVG_COMPONENTS.PROPERTY width={12} height={12} color={redesign.text.primary} />
                </View>
              )}
            </View>
          ) : (
            <>
              <View style={styles.badgesContainer}>
                {bulletin.pinHomepage && (
                  <View style={[styles.pinBadge, { backgroundColor: badgePillBg }]}>
                    <SVG_COMPONENTS.PROPERTY width={12} height={12} color={redesign.text.primary} />
                  </View>
                )}
                <View style={[styles.recipientBadge, { backgroundColor: badgePillBg }]}>
                  <Text style={[styles.recipientText, { color: redesign.text.primary }]}>
                    {getRecipientTypeLabel(bulletin.recipientType)}
                  </Text>
                </View>
                {!isScheduled && (
                  <View style={[styles.readCountBadge, { backgroundColor: badgePillBg }]}>
                    <SVG_COMPONENTS.EYE_OPEN width={14} height={14} color={redesign.text.muted} />
                    <Text style={[styles.readCountText, { color: redesign.text.muted }]}>
                      {bulletin.messageReadCount}
                    </Text>
                  </View>
                )}
              </View>
              {isEditable && onEdit && (
                <TouchableOpacity
                  style={styles.editButton}
                  onPress={(e) => {
                    e.stopPropagation();
                    onEdit(bulletin);
                  }}
                  activeOpacity={0.7}
                >
                  <SVG_COMPONENTS.PENCIL width={16} height={16} color={redesign.accent.blueBg} />
                </TouchableOpacity>
              )}
            </>
          )}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  touchableContainer: {
    marginBottom: 16,
  },
  cardContainer: {
    position: 'relative',
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
  },
  gradientBorder: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: 8,
  },
  contentContainer: {
    // bg + border come from GlassCard
  },
  contentContainerWithBorder: {
    margin: 2,
  },
  header: {
    marginBottom: 12,
  },
  headerTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    rowGap: 8,
  },
  leftSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 1,
    minWidth: 0,
  },
  rightSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flexShrink: 0,
  },
  dateText: {
    fontSize: 12,
    fontWeight: '400',
    flexShrink: 1,
  },
  badgesContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  pinBadge: {
    borderRadius: 12,
    padding: 6,
  },
  recipientBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  recipientText: {
    fontSize: 12,
    fontWeight: '500',
  },
  readCountBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  readCountText: {
    fontSize: 12,
    fontWeight: '500',
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
    lineHeight: 28,
    marginBottom: 8,
  },
  authorText: {
    fontSize: 12,
    fontWeight: '400',
    marginTop: 8,
  },
  editButton: {
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
